import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router";
import type { StaffWorkspace } from "../../../app/session";
import { api } from "../../../data";
import { Dialog, Money, PillView, SortTh, SortThDoppelt, useAction, useSorted } from "../../../components/ui";
import { date, money, monthLabel, numberLocale } from "../../../lib/format";
import { Auswahl, Feld, FormFehler, Haken, rechteText, Textfeld, ladeHerunter } from "../kalender/felder";
import { betragHinweis } from "../../../components/Erkannt";
import { aendereBuchung, legeBuchungAn, loescheBuchung, setzeBuchungsStatus } from "./daten";
import {
  ART_DURCHLAUFEND, BUCHUNGS_ARTEN, BUCHUNGS_ART_LABEL, LEDGER_ARTEN, LEDGER_STAENDE, plattformName, aufteilung, creatorAnteilVorgabe, csvBetrag, filtereLedger,
  formularAusBuchung, leereBuchung, ledgerSummen, monatLang, pruefeBuchung, standardSatz, talentName, toCsv,
  type BuchungsFormular, type KennzahlenDaten, type KzBooking, type LedgerArt, type LedgerStand, type LedgerZeile, type Umrechner,
} from "./model";
import { CURRENCIES } from "@talentkontor/domain";
import { t } from "../../../lib/sprache";
import { useAuslassungTitel } from "../../../lib/auslassung";

/**
 * Buchungen (bisheriges Portal AD-033 … AD-036): Monats-Timeline aus Kooperationsmonaten und manuellen Buchungen, filtern,
 * summieren je Währung, sortieren; manuelle Buchung anlegen, ändern, buchen, stornieren, löschen. Rechte prüft die Datenbank.
 */

type SortKey = "datum" | "position" | "kooperation" | "marke" | "creator" | "plattform" | "stand" | "betrag";

export interface BuchungsFilterZustand {
  art: LedgerArt | "alle";
  stand: LedgerStand | "alle";
  suche: string;
  monat: string | null;
}

/** Zeilen je Monat, bevor „Weitere zeigen“ erscheint */
const ZEILEN_JE_MONAT = 50;

export function Buchungen({ ws, d, zeilen, von, bis, label, waehrung, orgWaehrung, heute, convert, creatorId, markeId, filter, setFilter }: {
  ws: StaffWorkspace;
  d: KennzahlenDaten;
  zeilen: LedgerZeile[];
  von: string;
  bis: string;
  label: string;
  waehrung: string;
  orgWaehrung: string;
  heute: string;
  convert: Umrechner;
  creatorId: string;
  markeId: string;
  filter: BuchungsFilterZustand;
  setFilter: (f: Partial<BuchungsFilterZustand>) => void;
}) {
  const talents = useMemo(() => new Map(d.talents.map((t) => [t.id, t])), [d.talents]);
  const brands = useMemo(() => new Map(d.brands.map((b) => [b.id, b.name])), [d.brands]);
  const deals = useMemo(() => new Map(d.deals.map((x) => [x.id, x])), [d.deals]);
  const [dialog, setDialog] = useState<{ buchung: KzBooking | null; datum: string } | null>(null);
  // Praxistest K-152: je Monat zuerst nur die ersten Zeilen (3.852 Zeilen am Stück waren 62.741 Elemente auf der Seite).
  // Summen, Filter und CSV rechnen immer mit allen Zeilen.
  const [ganzeMonate, setGanzeMonate] = useState<ReadonlySet<string>>(new Set());
  // Kompakt (D-1): Nebenzeilen einzeilig, voller Text im Tooltip
  const kompakt = useAuslassungTitel<HTMLDivElement>();
  const suchtext = (z: LedgerZeile) => [
    monatLang(z.monat), z.label, z.hinweis, z.dealId ? deals.get(z.dealId)?.title : "", z.brandId ? brands.get(z.brandId) : "", talentName(talents.get(z.talentId ?? "")),
    z.plattformen.map((p) => plattformName(p, d.platforms)).join(" "), z.pille.label, z.buchung?.note,
  ].filter(Boolean).join(" ");
  const gefiltert = useMemo(
    () => filtereLedger(zeilen, { from: von, to: bis, art: filter.art, stand: filter.stand, talentId: creatorId, brandId: markeId, suche: filter.suche, monat: filter.monat }, suchtext),
    // suchtext hängt nur an den Stammdaten
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [zeilen, von, bis, filter, creatorId, markeId, talents, brands, deals],
  );
  const { sorted, sort, toggle } = useSorted<LedgerZeile, SortKey>(gefiltert, {
    datum: (z) => z.datum ?? `${z.monat}-00`,
    position: (z) => z.label,
    kooperation: (z) => (z.dealId ? deals.get(z.dealId)?.title ?? "" : ""),
    marke: (z) => (z.brandId ? brands.get(z.brandId) ?? "" : ""),
    creator: (z) => talentName(talents.get(z.talentId ?? "")),
    plattform: (z) => z.plattformen.join(","),
    stand: (z) => z.pille.label,
    betrag: (z) => z.umgerechnet,
  });
  // Monatsstruktur bleibt erhalten, sortiert wird innerhalb der Monate (wie im bisherigen Portal)
  const monate = [...new Set(gefiltert.map((z) => z.monat))].sort((a, b) => b.localeCompare(a));
  const s = ledgerSummen(gefiltert);
  const jeWaehrung = (art: LedgerArt) => {
    const m = s.jeArt[art];
    return m ? Object.entries(m).sort(([a], [b]) => a.localeCompare(b)).map(([w, c]) => money(c, w)).join(" · ") : "–";
  };
  const darfBuchen = ws.can("confirm_payments");
  const neu = (monat?: string) => setDialog({ buchung: null, datum: monat && monat !== heute.slice(0, 7) ? `${monat}-01` : heute });
  // Sprung aus den Monatsdetails der Übersicht: ?bmonat=YYYY-MM&neu=1 öffnet den Dialog einmal mit dem Monat
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    if (params.get("neu") !== "1") return;
    const m = params.get("bmonat");
    neu(m && /^\d{4}-\d{2}$/.test(m) ? m : undefined);
    const next = new URLSearchParams(params);
    next.delete("neu");
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const gefiltertAktiv = filter.art !== "alle" || filter.stand !== "alle" || !!filter.suche || !!filter.monat;

  function exportieren() {
    const kopf = [t("Monat", "Month"), t("Datum", "Date"), t("Position", "Item"), t("Kooperation", "Deal"), t("Marke", "Brand"), "Creator", t("Plattform", "Platform"), t("Stand", "Status"), t("Betrag", "Amount"), t("Währung", "Currency"), t(`Betrag ${waehrung}`, `Amount ${waehrung}`), t(`Agenturanteil ${waehrung}`, `Agency share ${waehrung}`), t("Im Umsatz", "In revenue"), t("Hinweis", "Note")];
    const zeilenCsv = sorted.map((z) => [
      z.monat, z.datum ?? "", z.label, z.dealId ? deals.get(z.dealId)?.title ?? "" : "", z.brandId ? brands.get(z.brandId) ?? "" : "",
      z.talentId ? talentName(talents.get(z.talentId)) : "", z.plattformen.map((p) => plattformName(p, d.platforms)).join(", "), z.pille.label, csvBetrag(z.betrag), z.waehrung,
      csvBetrag(z.umgerechnet), csvBetrag(z.agentur), z.imUmsatz ? t("Ja", "Yes") : t("Nein", "No"), [z.hinweis, z.buchung?.note].filter(Boolean).join(" · "),
    ]);
    ladeHerunter(`Buchungen_${von}_${bis}.csv`, toCsv([kopf, ...zeilenCsv]));
  }

  return (
    <section className="panel stack" id="buchungen" aria-labelledby="buchungen-titel">
      <div className="row between">
        <h3 id="buchungen-titel">{t("Buchungen", "Bookings")}</h3>
        <div className="row">
          <button type="button" onClick={exportieren} disabled={!sorted.length}>{t("Als CSV exportieren", "Export as CSV")}</button>
          <button type="button" className="primary" onClick={() => neu()}>{t("Buchung anlegen", "Create booking")}</button>
        </div>
      </div>
      <p>
        {t("Monats-Timeline für verbuchte, vorläufige und manuelle Positionen · ", "Monthly timeline for booked, provisional and manual items · ")}{filter.monat ? monatLang(filter.monat) : label}
        {filter.monat && <> · <button type="button" className="tk-kz-link" onClick={() => setFilter({ monat: null })}>{t("ganzen Zeitraum zeigen", "show the whole period")}</button></>}
      </p>
      <div className="toolbar" role="group" aria-label={t("Buchungen filtern", "Filter bookings")}>
        <input type="search" aria-label={t("Buchungen durchsuchen", "Search bookings")} placeholder={t("Kooperation, Marke, Creator oder Notiz suchen …", "Search deal, brand, creator or note …")} value={filter.suche}
          onChange={(e) => setFilter({ suche: e.target.value })} />
        <select aria-label={t("Nach Position filtern", "Filter by item")} value={filter.art} onChange={(e) => setFilter({ art: e.target.value as LedgerArt | "alle" })}>
          {LEDGER_ARTEN.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
        </select>
        <select aria-label={t("Nach Stand filtern", "Filter by status")} value={filter.stand} onChange={(e) => setFilter({ stand: e.target.value as LedgerStand | "alle" })}>
          {LEDGER_STAENDE.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
        </select>
      </div>
      <div className="row" role="group" aria-label={t("Summen der Auswahl", "Totals of the selection")}>
        <span className="pill"><span className="pill-zeichen">○</span> {t("Positionen: ", "Items: ")}{s.positionen}</span>
        <span className="pill"><span className="pill-zeichen">○</span> {t("Monate: ", "Months: ")}{s.monate}</span>
        <span className="pill"><span className="pill-zeichen">○</span> {t("Kooperationen: ", "Deals: ")}{s.kooperationen}</span>
        <span className="pill info"><span className="pill-zeichen">◇</span> {t("Budget: ", "Budget: ")}{jeWaehrung("budget")}</span>
        <span className="pill info"><span className="pill-zeichen">◇</span> {t("Zusatz: ", "Extras: ")}{jeWaehrung("zusatz")}</span>
        <span className="pill info"><span className="pill-zeichen">◇</span> {t("Agenturgebühren: ", "Agency fees: ")}{jeWaehrung("gebuehr")}</span>
        <span className="pill info"><span className="pill-zeichen">◇</span> {t("Provision: ", "Commission: ")}{jeWaehrung("provision")}</span>
        <span className="pill info"><span className="pill-zeichen">◇</span> {t("Manuell: ", "Manual: ")}{jeWaehrung("manuell")}</span>
        <span className={`pill ${s.offen ? "warning" : ""}`}><span className="pill-zeichen">{s.offen ? "◷" : "○"}</span> {t("Offen: ", "Open: ")}{s.offen}</span>
        <span className="pill success"><span className="pill-zeichen">✓</span> {t("Summe ", "Total ")}{waehrung}: {money(s.umgerechnet, waehrung)}</span>
        <span className="pill success"><span className="pill-zeichen">✓</span> {t("Agenturanteil: ", "Agency share: ")}{money(s.agentur, waehrung)}</span>
        {s.ohneKurs > 0 && <span className="pill warning"><span className="pill-zeichen">◷</span> {s.ohneKurs === 1 ? t("1 Position", "1 item") : t(`${s.ohneKurs} Positionen`, `${s.ohneKurs} items`)} {t("ohne Kurs", "without exchange rate")}</span>}
      </div>

      {monate.length ? (
        <div className="tablewrap tk-buchungsliste tk-sticky-end tk-rahmen tk-cardtable tk-handykarten" ref={kompakt}>
          {/* N-1: im schmalen Rahmen (bei 1024 px) steht der Stand unter der Position und der Knopf heißt kurz „Öffnen“; N-4: am Handy Karten */}
          <table>
            <caption className="sr-only">{t("Buchungen nach Monat", "Bookings by month")}</caption>
            <thead>
              <tr>
                <SortTh label={t("Monat / Datum", "Month / date")} k="datum" sort={sort} toggle={toggle} />
                <SortTh label={t("Position", "Item")} k="position" sort={sort} toggle={toggle} />
                {/* sieben statt neun Spalten (passt bei 1280 px): Plattform unter der Kooperation, Creator unter der Marke */}
                <SortThDoppelt label={t("Kooperation", "Deal")} k="kooperation" neben={t("Plattform", "Platform")} kNeben="plattform" sort={sort} toggle={toggle} />
                <SortThDoppelt label={t("Marke", "Brand")} k="marke" neben="Creator" kNeben="creator" sort={sort} toggle={toggle} />
                <SortTh label={t("Stand", "Status")} k="stand" sort={sort} toggle={toggle} className="tk-r-weg" />
                <SortTh label={t("Betrag", "Amount")} k="betrag" sort={sort} toggle={toggle} money />
                <th scope="col"><span className="sr-only">{t("Aktion", "Action")}</span></th>
              </tr>
            </thead>
            {monate.map((m) => {
              const imMonat = sorted.filter((z) => z.monat === m);
              const ms = ledgerSummen(imMonat);
              return (
                <tbody key={m}>
                  <tr className="tk-kz-monat">
                    <td colSpan={7}>
                      <div className="row between">
                        <span>
                          <strong>{monatLang(m)}</strong>
                          {" · "}{imMonat.length === 1 ? t("1 Position", "1 item") : t(`${imMonat.length} Positionen`, `${imMonat.length} items`)}
                          {" · "}{ms.kooperationen === 1 ? t("1 Kooperation", "1 deal") : t(`${ms.kooperationen} Kooperationen`, `${ms.kooperationen} deals`)}
                          {" · "}{t("Summe ", "Total ")}{money(ms.umgerechnet, waehrung)} {t("· Agenturanteil ", "· Agency share ")}{money(ms.agentur, waehrung)}
                          {m === heute.slice(0, 7) ? t(" · aktueller Monat", " · current month") : ""}
                        </span>
                        <button type="button" onClick={() => neu(m)} aria-label={t(`Buchung im ${monatLang(m)} hinzufügen`, `Add booking in ${monatLang(m)}`)}>{t("Buchung hinzufügen", "Add booking")}</button>
                      </div>
                    </td>
                  </tr>
                  {(ganzeMonate.has(m) ? imMonat : imMonat.slice(0, ZEILEN_JE_MONAT)).map((z) => {
                    const deal = z.dealId ? deals.get(z.dealId) : undefined;
                    return (
                      <tr key={z.key}>
                        <td data-label={t("Datum", "Date")}>{z.datum ? date(z.datum) : monatLang(z.monat)}</td>
                        <td className="tk-karte-titel">{z.label}{z.hinweis && <small className="tk-ell">{z.hinweis}</small>}{z.buchung?.note && <small className="tk-ell">{z.buchung.note}</small>}
                          <small className="tk-r-alt"><PillView pill={z.pille} /></small></td>
                        <td data-label={t("Kooperation", "Deal")}>{deal ? <Link to={`/agentur/kooperationen/${deal.id}?monat=${z.monat}`}>{deal.title}</Link> : <span className="muted">–</span>}
                          {z.plattformen.length > 0 && <small className="tk-ell">{z.plattformen.map((p) => plattformName(p, d.platforms)).join(", ")}</small>}</td>
                        <td data-label={t("Marke", "Brand")}>{z.brandId ? <Link to={`/agentur/marken/${z.brandId}`}>{brands.get(z.brandId) ?? "–"}</Link> : <span className="muted">–</span>}
                          {z.talentId && <small><Link to={`/agentur/creator/${z.talentId}`}>{talentName(talents.get(z.talentId))}</Link></small>}</td>
                        <td className="tk-r-weg" data-label={t("Stand", "Status")}><PillView pill={z.pille} /></td>
                        <td data-label={t("Betrag", "Amount")}>
                          {z.betrag === null ? <span className="muted">–</span> : <Money cents={z.betrag} currency={z.waehrung} />}
                          {z.betrag !== null && z.waehrung !== waehrung && (
                            <small>{z.umgerechnet === null ? t("Kein Kurs hinterlegt", "No exchange rate on file") : `≈ ${money(z.umgerechnet, waehrung)}`}</small>
                          )}
                          {z.agentur !== null && z.agentur !== z.umgerechnet && <small>{t("Agentur ", "Agency ")}{money(z.agentur, waehrung)}</small>}
                        </td>
                        <td>
                          {z.buchung ? (
                            <button type="button" onClick={() => setDialog({ buchung: z.buchung!, datum: z.buchung!.booked_on })}
                              aria-label={t(`${z.label} vom ${date(z.datum)} öffnen`, `Open ${z.label} of ${date(z.datum)}`)}>{t("Öffnen", "Open")}</button>
                          ) : deal ? (
                            <Link className="btn" to={`/agentur/kooperationen/${deal.id}?monat=${z.monat}`} aria-label={t(`Kooperation ${deal.title} öffnen`, `Open deal ${deal.title}`)}><span className="tk-r-lang">{t("Kooperation", "Deal")}</span><span className="tk-r-kurz">{t("Öffnen", "Open")}</span></Link>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                  {!ganzeMonate.has(m) && imMonat.length > ZEILEN_JE_MONAT && (
                    <tr className="tk-kz-mehr">
                      <td colSpan={7}>
                        <button type="button" onClick={() => setGanzeMonate((alt) => new Set([...alt, m]))}>
                          {t(`Weitere ${imMonat.length - ZEILEN_JE_MONAT} Positionen im ${monatLang(m)} zeigen`, `Show ${imMonat.length - ZEILEN_JE_MONAT} more items in ${monatLang(m)}`)}
                        </button>
                      </td>
                    </tr>
                  )}
                </tbody>
              );
            })}
          </table>
        </div>
      ) : (
        <div className="stack">
          <p>{gefiltertAktiv ? t("Keine passenden Ergebnisse. Passe die Suche oder die Filter an.", "No matching results. Adjust the search or the filters.") : t("In diesem Zeitraum gibt es keine Buchungen.", "There are no bookings in this period.")}</p>
          {gefiltertAktiv && <div className="row"><button type="button" onClick={() => setFilter({ art: "alle", stand: "alle", suche: "", monat: null })}>{t("Filter zurücksetzen", "Reset filters")}</button></div>}
        </div>
      )}

      <Dialog open={!!dialog} onClose={() => setDialog(null)} title={dialog?.buchung ? t(`Buchung · ${BUCHUNGS_ART_LABEL[dialog.buchung.kind] ?? dialog.buchung.kind}`, `Booking · ${BUCHUNGS_ART_LABEL[dialog.buchung.kind] ?? dialog.buchung.kind}`) : t("Buchung anlegen", "Create booking")} wide>
        {dialog && (
          <BuchungsDialog key={dialog.buchung?.id ?? `neu-${dialog.datum}`} buchung={dialog.buchung} datum={dialog.datum} d={d} ws={ws} darfBuchen={darfBuchen}
            orgWaehrung={orgWaehrung} convert={convert} onFertig={() => setDialog(null)} />
        )}
      </Dialog>
    </section>
  );
}

// ── Dialog: Buchung anlegen, ändern, buchen, stornieren, löschen (AD-035, AD-036) ──

function BuchungsDialog({ buchung, datum, d, ws, darfBuchen, orgWaehrung, convert, onFertig }: {
  buchung: KzBooking | null;
  datum: string;
  d: KennzahlenDaten;
  ws: StaffWorkspace;
  darfBuchen: boolean;
  orgWaehrung: string;
  convert: Umrechner;
  onFertig: () => void;
}) {
  const orgId = ws.org.org_id;
  const bearbeitbar = !buchung || buchung.status === "planned";
  const [f, setF] = useState<BuchungsFormular>(() => (buchung ? formularAusBuchung(buchung) : leereBuchung(datum, orgWaehrung, standardSatz(d, "", ""))));
  const [fehler, setFehler] = useState<Record<string, string>>({});
  // K-154 d: „Ein Creator-Anteil braucht einen Creator“ verschwindet, sobald Creator oder Kooperation gewählt sind
  useEffect(() => {
    if ((f.talentId || f.dealId) && fehler.creatorAnteil) setFehler(({ creatorAnteil: _weg, ...rest }) => rest);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.talentId, f.dealId]);
  const [frage, setFrage] = useState<"loeschen" | "stornieren" | null>(null);
  const { busy, error, run } = useAction();
  const set = <K extends keyof BuchungsFormular>(k: K) => (v: BuchungsFormular[K]) => setF((x) => ({ ...x, [k]: v }));
  // Laufende Kooperationen zuerst, mit Nummer und Zeitraum, damit gleichnamige unterscheidbar sind
  const ersterMonat = new Map<string, string>();
  for (const p of d.periods) if (!ersterMonat.has(p.deal_id) || p.month < ersterMonat.get(p.deal_id)!) ersterMonat.set(p.deal_id, p.month);
  const deals = [...d.deals].filter((x) => !x.archived_at || x.id === f.dealId)
    .sort((a, b) => Number(["done", "cancelled"].includes(a.status)) - Number(["done", "cancelled"].includes(b.status)) || (ersterMonat.get(b.id) ?? "").localeCompare(ersterMonat.get(a.id) ?? ""));
  const brandName = (id: string) => d.brands.find((b) => b.id === id)?.name ?? "–";
  // K-154 d: Kennung wie überall sonst (NT-D18), nicht „K-0018“
  const prefix = (d.settings as { invoice_number_prefix?: string | null } | null)?.invoice_number_prefix ?? null;
  const dealLabel = (x: (typeof deals)[number]) => [x.deal_number ? (prefix ? `${prefix}-D${x.deal_number}` : `D${x.deal_number}`) : null, `${brandName(x.brand_id)} · ${x.title}`,
    talentName(d.talents.find((t) => t.id === x.talent_id)), ersterMonat.get(x.id) ? `ab ${monthLabel(ersterMonat.get(x.id)!)}` : null].filter(Boolean).join(" · ");
  const durchlaufend = ART_DURCHLAUFEND.has(f.art);
  const teil = aufteilung(f);
  const waehrungen = [...new Set([orgWaehrung, ...(d.settings?.currencies ?? []), ...CURRENCIES])];
  const vorschau = teil && f.waehrung !== orgWaehrung ? convert(teil.agentur + teil.creator, f.waehrung, orgWaehrung, f.datum.slice(0, 7)) : null;

  function waehleDeal(id: string) {
    const deal = d.deals.find((x) => x.id === id);
    setF((x) => ({
      ...x, dealId: id, talentId: deal?.talent_id ?? x.talentId, brandId: deal?.brand_id ?? x.brandId, waehrung: deal?.currency ?? x.waehrung,
      agenturProzent: new Intl.NumberFormat(numberLocale(), { maximumFractionDigits: 2 }).format(standardSatz(d, id, deal?.talent_id ?? x.talentId, x.datum)),
    }));
  }

  async function speichern(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    if (buchung && !bearbeitbar) {
      const ok = await run("notiz", () => aendereBuchung(api, buchung.id, { note: f.notiz.trim() || null }), t("Notiz gespeichert.", "Note saved."));
      if (ok) onFertig();
      return;
    }
    const r = pruefeBuchung(f, { darfBuchen });
    setFehler(r.fehler);
    if (!r.daten) return;
    const daten = r.daten;
    const ok = buchung
      ? await run("speichern", async () => {
        const { status, ...rest } = daten;
        await aendereBuchung(api, buchung.id, rest);
        if (status === "booked") await setzeBuchungsStatus(api, buchung.id, "booked");
        return true;
      }, daten.status === "booked" ? t("Buchung gespeichert und gebucht.", "Booking saved and booked.") : t("Buchung gespeichert.", "Booking saved."))
      : await run("speichern", () => legeBuchungAn(api, orgId, daten), daten.status === "booked" ? t("Buchung angelegt und gebucht.", "Booking created and booked.") : t("Buchung als geplant angelegt.", "Booking created as planned."));
    if (ok) onFertig();
  }

  const status = buchung?.status;
  return (
    <form className="stack" onSubmit={speichern} noValidate aria-busy={!!busy}>
      {buchung && (
        <div className="row">
          <PillView pill={status === "booked" ? { label: t("Gebucht", "Booked"), tone: "success", symbol: "✓" } : status === "cancelled" ? { label: t("Storniert", "Cancelled"), tone: "neutral", symbol: "○" } : { label: t("Geplant", "Planned"), tone: "warning", symbol: "◷" }} />
          {buchung.booked_at && <small>{t("gebucht am ", "booked on ")}{date(buchung.booked_at, ws.org.timezone)}</small>}
          {buchung.source === "invoice" && <small>{t("aus einer Rechnung", "from an invoice")}</small>}
        </div>
      )}
      {!bearbeitbar && (
        <p>
          {status === "booked" ? t("Eine gebuchte Buchung ist fest. Korrekturen laufen über eine Gegenbuchung; stornieren ist möglich.", "A booked entry is fixed. Corrections are made via a counter-entry; cancelling is possible.") : t("Stornierte Buchungen bleiben unverändert.", "Cancelled bookings remain unchanged.")}
        </p>
      )}
      <div className="grid">
        <Auswahl label={t("Art", "Type")} value={f.art} disabled={!bearbeitbar} optionen={BUCHUNGS_ARTEN}
          onChange={(v) => setF((x) => ({ ...x, art: v as BuchungsFormular["art"], creatorAnteil: creatorAnteilVorgabe(v as BuchungsFormular["art"]) }))} />
        <Feld label={t("Datum", "Date")} type="date" value={f.datum} onChange={set("datum")} fehler={fehler.datum} disabled={!bearbeitbar} />
        <Auswahl label={t("Kooperation (optional)", "Deal (optional)")} value={f.dealId} onChange={waehleDeal} leer={t("Keine Kooperation", "No deal")} disabled={!bearbeitbar}
          optionen={deals.map((x) => ({ key: x.id, label: dealLabel(x) }))}
          hinweis={f.dealId ? t("Füllt Creator, Marke und Währung aus der Kooperation. Die Buchung zählt zusätzlich zum Umsatz der Kooperation, etwa eine Verlängerung der Nutzungsrechte oder ein Bonus.", "Fills in creator, brand and currency from the deal. The booking counts in addition to the deal's revenue, for example an extension of usage rights or a bonus.") : t("Füllt Creator, Marke und Währung aus der Kooperation.", "Fills in creator, brand and currency from the deal.")} />
        <Auswahl label={t("Creator (optional)", "Creator (optional)")} value={f.talentId} onChange={set("talentId")} leer={t("Kein Creator", "No creator")} disabled={!bearbeitbar || !!f.dealId}
          optionen={d.talents.map((tl) => ({ key: tl.id, label: `${talentName(tl)}${tl.status === "archived" ? t(" (archiviert)", " (archived)") : ""}` }))} />
        <Auswahl label={t("Marke (optional)", "Brand (optional)")} value={f.brandId} onChange={set("brandId")} leer={t("Keine Marke", "No brand")} disabled={!bearbeitbar || !!f.dealId}
          optionen={d.brands.map((b) => ({ key: b.id, label: b.name }))} />
        <Auswahl label={t("Währung", "Currency")} value={f.waehrung} onChange={set("waehrung")} fehler={fehler.waehrung} disabled={!bearbeitbar}
          optionen={waehrungen.map((w) => ({ key: w, label: w }))} />
        <Feld label={t("Betrag netto", "Net amount")} value={f.betrag} onChange={set("betrag")} fehler={fehler.betrag} inputMode="decimal" placeholder="1.200,00" disabled={!bearbeitbar}
          hinweis={[f.art === "correction" ? t("Negativ für Korrekturen und Gutschriften, zum Beispiel -150,00.", "Negative for corrections and credits, for example -150.00.") : null,
            betragHinweis(f.betrag, f.waehrung, true)?.text].filter(Boolean).join(" ") || undefined} />
        {f.art !== "agency_fee" && !durchlaufend && (
          <Feld label={t("Agenturanteil in %", "Agency share in %")} value={f.creatorAnteil ? f.agenturProzent : "100"} onChange={set("agenturProzent")} fehler={fehler.agenturProzent}
            inputMode="decimal" disabled={!bearbeitbar || !f.creatorAnteil} hinweis={t("Vorschlag aus Kooperation, Creator oder Agentur-Standard.", "Suggestion from deal, creator or agency default.")} />
        )}
      </div>
      {f.art !== "agency_fee" && !durchlaufend && (
        <Haken label={t("Creator erhält einen Anteil", "Creator receives a share")} checked={f.creatorAnteil} onChange={set("creatorAnteil")} disabled={!bearbeitbar}
          hinweis={fehler.creatorAnteil ?? (f.creatorAnteil ? undefined : t("Der ganze Betrag zählt als Agenturumsatz.", "The whole amount counts as agency revenue."))} />
      )}
      {durchlaufend && <p className="muted">{t("Eine Auslage läuft durch: Sie wird erstattet und zählt nicht als Agenturumsatz.", "An expense is passed through: it is reimbursed and does not count as agency revenue.")}</p>}
      <div className="notice" aria-live="polite">
        {teil ? (
          <>{t("Aufteilung: Agentur ", "Split: agency ")}{money(teil.agentur, f.waehrung)} {t("· Creator ", "· creator ")}{money(teil.creator, f.waehrung)}</>
        ) : (
          <>{t("Aufteilung erscheint, sobald Betrag und Satz stimmen.", "The split appears as soon as amount and rate are valid.")}</>
        )}
        {f.waehrung !== orgWaehrung && teil && (
          <><br />{vorschau === null ? t(`Kein Kurs ${f.waehrung}/${orgWaehrung} hinterlegt; ohne Kurs bleibt der Betrag in ${orgWaehrung} leer.`, `No exchange rate ${f.waehrung}/${orgWaehrung} on file; without a rate the amount in ${orgWaehrung} stays empty.`) : t(`≈ ${money(vorschau, orgWaehrung)} zum letzten hinterlegten Kurs; beim Buchen friert die Datenbank den Kurs ein.`, `≈ ${money(vorschau, orgWaehrung)} at the last rate on file; when booking, the database freezes the rate.`)}</>
        )}
      </div>
      <Haken label={t("Für den Creator sichtbar", "Visible to the creator")} checked={f.sichtbar} onChange={set("sichtbar")} disabled={!bearbeitbar || (!f.talentId && !f.dealId)}
        hinweis={fehler.sichtbar ?? t("Der Creator sieht die Buchung mit seinem Anteil in seinem Portal.", "The creator sees the booking with their share in their portal.")} />
      <Textfeld label={t("Notiz", "Note")} value={f.notiz} onChange={set("notiz")} fehler={fehler.notiz} maxLength={2000} rows={2} />
      {bearbeitbar && (
        <>
          <Haken label={t("Sofort buchen", "Book immediately")} checked={f.sofortBuchen} onChange={set("sofortBuchen")} disabled={!darfBuchen}
            hinweis={darfBuchen ? t("Ohne Haken bleibt die Buchung geplant und zählt als vorläufig.", "Without the tick the booking stays planned and counts as provisional.") : rechteText()} />
          {fehler.sofortBuchen && <small className="tk-error">{fehler.sofortBuchen}</small>}
        </>
      )}
      <FormFehler fehler={fehler} error={error} />
      <div className="row">
        {status !== "cancelled" && (
          <button className="primary" type="submit" disabled={!!busy}>{busy === "speichern" || busy === "notiz" ? t("Wird gespeichert …", "Saving …") : buchung ? t("Speichern", "Save") : t("Buchung anlegen", "Create booking")}</button>
        )}
        {buchung && status === "planned" && (
          <>
            <button type="button" disabled={!darfBuchen || !!busy} aria-describedby={darfBuchen ? undefined : "buchung-rechte"}
              onClick={() => void run("buchen", () => setzeBuchungsStatus(api, buchung.id, "booked"), t("Buchung gebucht.", "Booking booked.")).then((r) => r && onFertig())}>{t("Buchen", "Book")}</button>
            <button type="button" onClick={() => setFrage("loeschen")} disabled={!!busy}>{t("Löschen", "delete")}</button>
          </>
        )}
        {buchung && status === "booked" && (
          <button type="button" disabled={!darfBuchen || !!busy} aria-describedby={darfBuchen ? undefined : "buchung-rechte"} onClick={() => setFrage("stornieren")}>{t("Stornieren", "Cancel booking")}</button>
        )}
        <button type="button" onClick={onFertig}>{t("Abbrechen", "Cancel")}</button>
      </div>
      {!darfBuchen && buchung && status !== "cancelled" && <p id="buchung-rechte">{rechteText()}</p>}
      {frage && buchung && (
        <div className="notice stack" role="alert">
          <p>
            {frage === "loeschen"
              ? t("Die geplante Buchung wird gelöscht. Das lässt sich nicht rückgängig machen.", "The planned booking will be deleted. This cannot be undone.")
              : t(`Die Buchung über ${money(buchung.amount_cents, buchung.currency)} wird storniert und zählt nicht mehr. Die Notiz wird mitgespeichert.`, `The booking of ${money(buchung.amount_cents, buchung.currency)} will be cancelled and no longer counts. The note is saved as well.`)}
          </p>
          <div className="row">
            <button type="button" className="danger" disabled={!!busy} onClick={() => void run(frage, async () => {
              if (frage === "loeschen") await loescheBuchung(api, buchung.id);
              else await setzeBuchungsStatus(api, buchung.id, "cancelled", f.notiz.trim() || null);
              return true;
            }, frage === "loeschen" ? t("Buchung gelöscht.", "Booking deleted.") : t("Buchung storniert.", "Booking cancelled.")).then((r) => {
              setFrage(null);
              if (r) onFertig();
            })}>{busy ? t("Wird ausgeführt …", "Processing …") : frage === "loeschen" ? t("Endgültig löschen", "Delete permanently") : t("Stornieren bestätigen", "Confirm cancellation")}</button>
            <button type="button" onClick={() => setFrage(null)}>{t("Abbrechen", "Cancel")}</button>
          </div>
        </div>
      )}
    </form>
  );
}

